import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { socketService } from '../services/socketService';

// Types
export interface SocketContextType {
  isConnected: boolean;
  connect: (token: string) => void;
  disconnect: () => void;
  emit: (event: string, data?: any) => void;
  on: (event: string, callback: (data: any) => void) => void;
  off: (event: string, callback?: (data: any) => void) => void;
}

// Context
const SocketContext = createContext<SocketContextType | undefined>(undefined);

// Provider Props
interface SocketProviderProps {
  children: ReactNode;
}

// Provider Component
export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const { user, token } = useAuth();

  // Connect/disconnect based on authentication status
  useEffect(() => {
    if (user && token) {
      socketService.connect(token);
    } else {
      socketService.disconnect();
    }

    return () => {
      socketService.disconnect();
    };
  }, [user, token]);

  // Connect to socket
  const connect = (authToken: string): void => {
    socketService.connect(authToken);
  };

  // Disconnect from socket
  const disconnect = (): void => {
    socketService.disconnect();
  };

  // Emit event
  const emit = (event: string, data?: any): void => {
    socketService.emit(event, data);
  };

  // Listen to event
  const on = (event: string, callback: (data: any) => void): void => {
    socketService.on(event, callback);
  };

  // Remove event listener
  const off = (event: string, callback?: (data: any) => void): void => {
    socketService.off(event, callback);
  };

  const value: SocketContextType = {
    isConnected: socketService.isConnected(),
    connect,
    disconnect,
    emit,
    on,
    off,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

// Hook
export const useSocket = (): SocketContextType => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export default SocketContext;